"use client";

import { useSyncExternalStore } from "react";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { formatCurrency } from "@configs/currencies";
import { useOrder } from "@contexts/index";
import { fetchOrder } from "src/api/order.api";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { orderQueryKey } from "src/hooks/useOrder";
import { usePathname } from "src/i18n/navigation";
import { creditApi } from "src/services/api";
import type { CreditLotView } from "src/utils/credit-lots";
import {
  creditCurrencyOf,
  creditRibbonAllowed,
  creditRibbonMessages,
  creditRibbonView,
  reminderLotsFromBalance,
  reminderLotsFromMine,
  ribbonTokenUsable,
} from "src/utils/credit-reminder";
import { readStoredCreditToken, subscribeToCreditToken } from "src/utils/credit-token";
import { getMarketDomain } from "src/utils/domain.util";
import { isEmbeddedInApp } from "src/utils/embedded-app";
import { shortDayMonth } from "src/utils/market-time";
import {
  isSignedIn as readSignedIn,
  sessionKey as readSessionKey,
  subscribeToSignIn,
} from "src/utils/signed-in";
import {
  useClientValue,
  useShopCurrency,
  useShopTimeZone,
} from "@components/cashback/use-client-value";

/** The owner's ribbon colours: brand teal, white text, in both modes. */
const RIBBON_BG = "#2D6F7A";
const RIBBON_INK = "#FFFFFF";

/** A gift box, as a line icon in the text colour. */
function GiftIcon() {
  return (
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{ width: { xs: 18, sm: 20 }, height: { xs: 18, sm: 20 }, flexShrink: 0 }}
    >
      <rect x="3" y="8" width="18" height="13" rx="2" />
      <path d="M12 8v13M3 12h18M12 8c-2.5 0-4.5-1.2-4.5-3s2.5-2 4.5 3c2-5 4.5-4.8 4.5-3s-2 3-4.5 3" />
    </Box>
  );
}

/**
 * "Na računu imate 10,00 € dobroimetja · za naročila od 30,00 € naprej, do
 * 10. 11." — a thin strip under the shop header for a customer who has credit.
 *
 * It only informs: credit comes off at checkout by itself once the order
 * qualifies, so there is nothing to click. The figures are the server's live
 * lots for THIS shop (`creditRibbonView`):
 *
 *  - signed in → GET /credit/mine, under the same query key as the account
 *    page's `MyCreditBox`, so the two share one read and never disagree;
 *  - a guest holding a claim token (the email link, or a typed gift card) →
 *    GET /credit/balance with that token, and with the cart's id so credit
 *    already held on the cart still counts (`ribbonTokenUsable` decides
 *    whether the token may be used here at all);
 *  - anyone else → nothing, and no request.
 *
 * Renders nothing — no height at all — without credit, on any failure
 * (including an unconfirmed account; the account page explains that), in the
 * app's WebView, on admin pages and on the checkout, which shows the credit
 * itself (`creditRibbonAllowed`). Everything is read on the client only, so
 * the server render and hydration agree on "nothing".
 */
export default function CreditReminderRibbon() {
  const t = useAppTranslate();
  const locale = useLocale();
  const pathname = usePathname();
  const shopCurrency = useShopCurrency();
  const timeZone = useShopTimeZone();
  const market = useClientValue<string | null>(getMarketDomain, null);
  const embedded = useClientValue<boolean>(isEmbeddedInApp, false);
  const allowed = creditRibbonAllowed({ pathname, embedded });

  const signedIn = useSyncExternalStore(subscribeToSignIn, readSignedIn, () => false);
  const sessionKey = useSyncExternalStore(subscribeToSignIn, readSessionKey, () => null);
  const heldToken = useSyncExternalStore(
    subscribeToCreditToken,
    () => readStoredCreditToken(),
    () => null,
  );

  // The cart as the rest of the page already has it — read from the cache,
  // never fetched from here: the header is on every page, and the cart's
  // email only narrows which token may be used.
  const { mainOrderId, guestToken } = useOrder();
  const { data: cart } = useQuery({
    queryKey: orderQueryKey(mainOrderId ?? ""),
    queryFn: () => fetchOrder(mainOrderId as string, guestToken as string),
    enabled: false,
  });
  const tokenUsable = !signedIn && ribbonTokenUsable(heldToken, cart?.shippingEmail);

  const mine = useQuery({
    queryKey: ["my-credit", sessionKey],
    enabled: allowed && signedIn && sessionKey !== null,
    queryFn: async () => (await creditApi.creditControllerMine()).data,
    retry: false,
    staleTime: 30_000,
  });
  const ledgerCurrency = creditCurrencyOf(shopCurrency);
  const balance = useQuery({
    // The token is read inside the fetch and kept out of the key, as in
    // `useOrderCredit`, so it never sits in the query cache.
    queryKey: ["credit-reminder-balance", ledgerCurrency, mainOrderId ?? null],
    enabled: allowed && tokenUsable && ledgerCurrency !== "",
    queryFn: async () => {
      const token = readStoredCreditToken() ?? undefined;
      const { data } = await creditApi.creditControllerBalance(
        ledgerCurrency,
        token,
        mainOrderId ?? undefined,
      );
      return Array.isArray(data.lots) ? (data.lots as CreditLotView[]) : [];
    },
    retry: false,
    staleTime: 30_000,
  });

  if (!allowed) return null;
  const lots = signedIn
    ? mine.isSuccess
      ? reminderLotsFromMine(mine.data?.lots)
      : []
    : tokenUsable && balance.isSuccess
      ? reminderLotsFromBalance(balance.data)
      : [];
  const view = creditRibbonView({ lots, shopCurrency, market });
  if (!view) return null;

  const money = (cents: number) => formatCurrency(cents / 100, view.currency);
  // The short day and month, in the shop market's zone — the zone the
  // account page and the credit email date it in.
  const date = view.expiresAt
    ? shortDayMonth(new Date(view.expiresAt), locale === "en" ? "en-GB" : locale, timeZone)
    : "";
  const { amount, detail } = creditRibbonMessages(view, money, date);
  const amountText = t(amount.key, amount.fallback, amount.params);
  const detailText = t(detail.key, detail.fallback, detail.params);

  return (
    <Box
      role="status"
      data-testid="credit-reminder-ribbon"
      sx={{
        bgcolor: RIBBON_BG,
        color: RIBBON_INK,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: { xs: 1, sm: 1.5 },
        px: 2,
        py: { xs: 0.75, sm: 1 },
      }}
    >
      <GiftIcon />
      <Typography
        component="p"
        sx={{
          fontSize: { xs: 13, sm: 14.5 },
          lineHeight: 1.35,
          color: "inherit",
          minWidth: 0,
        }}
      >
        <Box component="strong" sx={{ fontWeight: 700 }}>
          {amountText}
        </Box>
        {` · ${detailText}`}
      </Typography>
    </Box>
  );
}
